import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { ensureIdentity } from '../../../features/auth/useAuth';
import { ADMIN_MFA_ENFORCED } from '../../../features/identity/mfaFlag';
import { AdminLayout } from '../../../layouts/AdminLayout';

/**
 * Spec 12.1: platform admin. Spec 11.2 keeps it in the same application but as "a
 * separately code-split bundle behind a role guard", so players never download it.
 *
 * Spec 10.3 requires MFA on this role. An unenrolled admin is sent to their profile with a
 * note rather than into an area where every request would fail — but `adminProcedure` is
 * still the enforcement point, and it checks BOTH enrolment and this session's verification
 * (spec 10.8: what the UI hides is never the check).
 */
export const Route = createFileRoute('/$lang/admin')({
  beforeLoad: async ({ context, params, location }) => {
    const identity = await ensureIdentity(context.queryClient);

    if (identity === null || identity.isGuest) {
      throw redirect({
        to: '/$lang/login',
        params: { lang: params.lang },
        search: { returnTo: location.pathname },
      });
    }

    if (identity.role !== 'platform_admin') {
      /*
       * Redirected rather than shown a 403: telling someone an admin area exists here is
       * information they have no use for.
       */
      throw redirect({ to: '/$lang/app', params: { lang: params.lang } });
    }

    if (ADMIN_MFA_ENFORCED && !identity.mfaEnrolled) {
      /*
       * Sent to where enrolment lives — Studio settings (spec 10.3's `MfaCard`). Without
       * this an admin would land on a working-looking area whose every request answers
       * MFA_REQUIRED, with nothing saying where to go. Skipped while MFA is off (`mfaFlag`).
       *
       * Only enrolment is checked here: whether THIS session is verified is not on
       * `IdentityDto`, and putting it there would make an identity response vary by session
       * in a way nothing else does. The server catches that case, and `MfaCard` prompts for
       * the code.
       */
      throw redirect({ to: '/$lang/studio/settings', params: { lang: params.lang } });
    }
  },
  component: AdminArea,
});

function AdminArea(): React.ReactNode {
  const { lang } = Route.useParams();
  return (
    <AdminLayout lang={lang}>
      <Outlet />
    </AdminLayout>
  );
}
